// frontend/src/components/VehicleList.js - Professional themed vehicle management
import { useState, useEffect, useCallback } from 'react';
import axios from '../axiosConfig';
import { Edit, Delete, BookX, Save, Plus, Search, Image as ImageIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getGlobalThreshold } from '../utils/preferences';
import { useTheme } from '../contexts/ThemeContext';
import { usePreferences } from '../contexts/PreferenceContext';
import { ThemedButton, ThemedCard, ThemedInput } from './themed';
import gpsDeviceService from '../services/gpsDeviceService';
import AppImage from './common/AppImage';
import VehicleAssignmentPolicyPanel from './vehicle-assignment/VehicleAssignmentPolicyPanel';
import ConfirmDialogShell from './dialogs/ConfirmDialogShell';
import OperationalUnavailabilityPanel from './operational-unavailability/OperationalUnavailabilityPanel';

const VehicleList = ({ currentEntity }) => {
  const { background, text, border, components, cn } = useTheme();
  const { t } = useTranslation();
  const { entitySettings } = usePreferences();
  const globalThreshold = getGlobalThreshold();
  const accessibleServiceEnabled =
    entitySettings?.accessible_service_enabled === true ||
    entitySettings?.accessible_service_enabled === 'true' ||
    entitySettings?.accessible_service_enabled === 1 ||
    entitySettings?.accessible_service_enabled === '1';
  
  // State management
  const [vehicles, setVehicles] = useState([]);
  const [newVehicle, setNewVehicle] = useState({
    vehicle_number: '',
    capacity: '',
    luggage_capacity: '',
    accessible: false,
    accessible_capacity: '',
    accessible_seat_usage: 'reserved_only',
    gps_device_id: ''
  });
  // Tab state: 'vehicles' | 'policy'
  const [activeTab, setActiveTab] = useState('vehicles');
  const [editingVehicleId, setEditingVehicleId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [vehiclePicture, setVehiclePicture] = useState(null);
  const [loading, setLoading] = useState(true);

  // GPS Device state
  const [availableDevices, setAvailableDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [allDevices, setAllDevices] = useState([]); // For showing device info in vehicle list

  /**
   * Load all GPS devices for the entity (for displaying in vehicle list)
   */
  const loadGPSDevices = useCallback(async (entityId) => {
    try {
      const devices = await gpsDeviceService.getAllDevices(entityId);
      setAllDevices(devices);
    } catch {
      // Non-blocking error - vehicles can still be managed without GPS data
    }
  }, []);

  /**
   * Load available GPS devices for assignment dropdown
   * Called when opening add/edit form
   */
  const loadAvailableDevices = useCallback(async (entityId, vehicleId = null) => {
    try {
      setDevicesLoading(true);
      const devices = await gpsDeviceService.getAvailableDevices(entityId, vehicleId);
      setAvailableDevices(devices);
    } catch {
      toast.error(t('errorLoadingGPSDevices'));
      setAvailableDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  }, [t]);

  const fetchVehicles = useCallback(async (entityId) => {
    try {
      setLoading(true);
      if (!entityId) {
        setVehicles([]);
        return;
      }
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/vehicles/${entityId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setVehicles(response.data);    
    } catch {
      toast.error(t('errorFetchingVehicles'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (currentEntity && currentEntity.entityId) {
      fetchVehicles(currentEntity.entityId);
      loadGPSDevices(currentEntity.entityId);
    }
  }, [currentEntity, fetchVehicles, loadGPSDevices]);
  
  // Add this function to handle file selection
  const handleFileChange = (event) => {
    setVehiclePicture(event.target.files[0]);
  };

  const handleVehicleSelect = useCallback((vehicleId) => {
    setSelectedVehicleId(prevId => prevId === vehicleId ? null : vehicleId);
    setShowAddForm(false);
    // setEditingVehicleId(null);
    // setNewVehicle({ vehicle_number: '', capacity: '' });
  }, []);
  

  const handleInputChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    if (e.target.name === 'accessible' && !value) {
      setNewVehicle({
        ...newVehicle,
        accessible: false,
        accessible_capacity: '',
        accessible_seat_usage: 'reserved_only'
      });
      return;
    }
    setNewVehicle({ ...newVehicle, [e.target.name]: value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    // setErrorMessage('');
    // setSuccessMessage('');

    // Client-side validation
    if (!newVehicle.vehicle_number.trim()) {
      toast.error(t('vehicleNumberRequired'));
      return;
    }

    const capacity = parseInt(newVehicle.capacity, 10);
    const luggageCapacity = parseInt(newVehicle.luggage_capacity, 10);
    const accessibleCapacity = newVehicle.accessible
      ? parseInt(newVehicle.accessible_capacity || '0', 10)
      : 0;

    if (isNaN(capacity) || capacity < 0) {
      toast.error(t('invalidCapacity'));
      return;
    }

    if (isNaN(luggageCapacity) || luggageCapacity < 0) {
      toast.error(t('invalidLuggageCapacity'));
      return;
    }

    if (newVehicle.accessible && (isNaN(accessibleCapacity) || accessibleCapacity < 0)) {
      toast.error(t('invalidAccessibleCapacity'));
      return;
    }

    if (newVehicle.accessible && !['reserved_only', 'shared_with_standard'].includes(newVehicle.accessible_seat_usage)) {
      toast.error(t('invalidAccessibleSeatUsage'));
      return;
    }

    const formData = new FormData();
    formData.append('vehicle_number', newVehicle.vehicle_number);
    formData.append('capacity', newVehicle.capacity || '0');
    formData.append('luggage_capacity', newVehicle.luggage_capacity || '0');
    formData.append('accessible', newVehicle.accessible ? 'true' : 'false');
    if (accessibleServiceEnabled) {
      formData.append('accessible_capacity', newVehicle.accessible ? (newVehicle.accessible_capacity || '0') : '0');
      formData.append('accessible_seat_usage', newVehicle.accessible ? newVehicle.accessible_seat_usage : 'reserved_only');
    }
    if (vehiclePicture) {
      formData.append('picture', vehiclePicture);
    }
    try {
      let response;
      let savedVehicleId;

      // Step 1: Save vehicle data
      if (editingVehicleId) {
        response = await axios.put(`/api/vehicles/${currentEntity.entityId}/${editingVehicleId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        savedVehicleId = editingVehicleId;
        toast.success(`${newVehicle.vehicle_number} ${t('vehicleUpdated')}`);
      } else {
        response = await axios.post(`/api/vehicles/${currentEntity.entityId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        savedVehicleId = response.data?.data?.id;
        toast.success(`${newVehicle.vehicle_number} ${t('vehicleCreated')}`);
      }

      // Step 2: Handle GPS device assignment (if changed)
      const selectedDeviceId = newVehicle.gps_device_id;
      const previousDevice = editingVehicleId
        ? allDevices.find(d => d.vehicle_id === editingVehicleId)
        : null;
      const previousDeviceId = previousDevice?.id || null;

      // Check if device assignment changed
      if (selectedDeviceId !== previousDeviceId) {
        try {
          if (selectedDeviceId && selectedDeviceId !== 'none') {
            // Assign new device
            await gpsDeviceService.assignToVehicle(
              currentEntity.entityId,
              parseInt(selectedDeviceId),
              savedVehicleId,
              false // setAsDefault - can be enhanced later
            );
          } else if (previousDeviceId && (selectedDeviceId === 'none' || !selectedDeviceId)) {
            // Unassign previous device
            await gpsDeviceService.unassignFromVehicle(
              currentEntity.entityId,
              previousDeviceId
            );
          }
          // Reload devices to update available list
          await loadGPSDevices(currentEntity.entityId);
        } catch {
          toast.warning(t('vehicleSavedButDeviceAssignmentFailed'));
        }
      }

      // Step 3: Refresh and cleanup
      fetchVehicles(currentEntity.entityId);
      setNewVehicle({
        vehicle_number: '',
        capacity: '',
        luggage_capacity: '',
        accessible: false,
        accessible_capacity: '',
        accessible_seat_usage: 'reserved_only',
        picture_url: '',
        gps_device_id: ''
      });
      setEditingVehicleId(null);
      setShowAddForm(false);
      handleVehicleSelect(null);
      setVehiclePicture(null);
      setAvailableDevices([]);
    } catch (error) {
      if (error.response && error.response.status === 400) {
        toast.error(
          error.response.data.message ||
          error.response.data.error ||
          `${newVehicle.vehicle_number} ${t('vehicleAlreadyExist')}`
        );
      } else {
        toast.error(t('errorSavingVehicle'));
      }
    }
  };

  const handleEdit = (vehicle) => {
    // Find currently assigned GPS device for this vehicle
    const assignedDevice = allDevices.find(d => d.vehicle_id === vehicle.id);

    setNewVehicle({
      vehicle_number: vehicle.vehicle_number,
      capacity: vehicle.capacity,
      luggage_capacity: vehicle.luggage_capacity,
      accessible: Boolean(vehicle.is_accessible),
      accessible_capacity: vehicle.accessible_capacity ?? '',
      accessible_seat_usage: vehicle.accessible_seat_usage || 'reserved_only',
      picture_url: vehicle.picture_url,
      gps_device_id: assignedDevice ? assignedDevice.id.toString() : ''
    });
    setEditingVehicleId(vehicle.id);
    setSelectedVehicleId(vehicle.id);
    setShowAddForm(true);

    // Load available devices for this vehicle (includes currently assigned device)
    if (currentEntity?.entityId) {
      loadAvailableDevices(currentEntity.entityId, vehicle.id);
    }
  };

  const handleDeleteClick = (vehicle) => {
    setVehicleToDelete(vehicle);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = useCallback(async () => {
    if (vehicleToDelete) {
      try {
        setIsDeleting(true);
        await axios.delete(`/api/vehicles/${currentEntity.entityId}/${vehicleToDelete.id}`);
        fetchVehicles(currentEntity.entityId);
        toast.success(`${vehicleToDelete.vehicle_number} ${t('vehicleDeleted')}`);
      } catch {
        toast.error(t('errorDeletingVehicle'));
      } finally {
        setIsDeleting(false);
        setShowDeleteConfirmation(false);
        setVehicleToDelete(null);
      }
    }
  }, [vehicleToDelete, currentEntity.entityId, t, fetchVehicles]);

  const handleCancel = () => {
    setNewVehicle({
      vehicle_number: '',
      capacity: '',
      luggage_capacity: '',
      accessible: false,
      accessible_capacity: '',
      accessible_seat_usage: 'reserved_only',
      picture_url: '',
      gps_device_id: ''
    });
    setEditingVehicleId(null);
    setShowAddForm(false);
    setAvailableDevices([]);
    setVehiclePicture(null);
  };

  const handleAddVehicleClick = () => {
    setShowAddForm(true);
    setSelectedVehicleId(null); // Deselect any selected shuttle
    setEditingVehicleId(null); // Reset editing state
    setNewVehicle({
      vehicle_number: '',
      capacity: '',
      luggage_capacity: '',
      accessible: false,
      accessible_capacity: '',
      accessible_seat_usage: 'reserved_only',
      picture_url: '',
      gps_device_id: ''
    }); // Reset form fields
    setVehiclePicture(null);

    // Load available devices for new vehicle
    if (currentEntity?.entityId) {
      loadAvailableDevices(currentEntity.entityId, null);
    }
  };

  // Clear messages after 5 seconds
/*   useEffect(() => {
    const timer = setTimeout(() => {
      setErrorMessage('');
      setSuccessMessage('');
    }, 5000);
    return () => clearTimeout(timer);
  }, [errorMessage, successMessage]); */

  // Filter shuttles based on search term
  const filteredVehicles = vehicles.filter(vehicle =>
    vehicle.vehicle_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vehicle.capacity.toString().includes(searchTerm)
  );

  return (
    <div className={cn('flex flex-col h-screen', background.primary)} style={{ height: 'calc(100vh - 54px)' }}>
      {/* Tab bar */}
      <div className={cn('flex border-b', border.default)}>
        <button
          onClick={() => setActiveTab('vehicles')}
          className={cn(
            'px-6 py-3 text-sm font-medium transition-colors',
            activeTab === 'vehicles'
              ? cn('border-b-2 border-blue-500', 'text-blue-600 dark:text-blue-400')
              : cn(text.muted, 'hover:text-gray-700 dark:hover:text-gray-300')
          )}
        >
          {t('vehicleAssignment.vehiclesTab')}
        </button>
        <button
          onClick={() => setActiveTab('policy')}
          className={cn(
            'px-6 py-3 text-sm font-medium transition-colors',
            activeTab === 'policy'
              ? cn('border-b-2 border-blue-500', 'text-blue-600 dark:text-blue-400')
              : cn(text.muted, 'hover:text-gray-700 dark:hover:text-gray-300')
          )}
        >
          {t('vehicleAssignment.policyTab')}
        </button>
      </div>

      {/* Auto-Assign Policy Tab content */}
      {activeTab === 'policy' && (
        <div className={cn('flex-grow overflow-y-auto p-6', background.secondary)}>
          {selectedVehicleId ? (
            <VehicleAssignmentPolicyPanel
              vehicleId={selectedVehicleId}
              entityId={currentEntity?.entityId}
              vehicle={vehicles.find(v => v.id === selectedVehicleId)}
            />
          ) : (
            <div className={cn('text-center py-12', text.muted)}>
              {t('vehicleAssignment.noVehicleSelected')}
            </div>
          )}
        </div>
      )}

      {/* Vehicles Tab content — only rendered when activeTab === 'vehicles' */}
      {activeTab === 'vehicles' && <>

      {/* Header Section */}
      <div className={cn('p-6 border-b', border.default)}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <h2 className={cn('text-2xl font-bold', text.primary)}>
              {t('vehicles')} ({vehicles.length})
            </h2>
            {vehicles.length >= globalThreshold && (
              <div className="relative">
                <Search className={cn('absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4', text.muted)} />
                <ThemedInput
                  type="text"
                  placeholder={t('searchVehicles')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
            )}
          </div>
          <ThemedButton
            onClick={handleAddVehicleClick}
            icon={Plus}
            variant="primary"
          >
            {t('addVehicle')}
          </ThemedButton>
        </div>

        {showAddForm && (
          <ThemedCard className="mt-6">
            <ThemedCard.Header>
              {editingVehicleId ? t('editVehicle') : t('addVehicle')}
            </ThemedCard.Header>
            <ThemedCard.Body>
              <form onSubmit={handleSubmit} encType="multipart/form-data" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className={cn('block text-sm font-medium mb-2', text.secondary)}>
                      {t('vehicleNumber')} *
                    </label>
                    <ThemedInput
                      type="text"
                      name="vehicle_number"
                      value={newVehicle.vehicle_number}
                      onChange={handleInputChange}
                      placeholder={t('vehicleNumber')}
                      required
                    />
                  </div>
                  <div>
                    <label className={cn('block text-sm font-medium mb-2', text.secondary)}>
                      {t('capacity')} *
                    </label>
                    <ThemedInput
                      type="number"
                      name="capacity"
                      value={newVehicle.capacity}
                      onChange={handleInputChange}
                      placeholder={t('capacity, 0 for unlimited')}
                      required
                      min="0"
                    />
                  </div>
                  <div>
                    <label className={cn('block text-sm font-medium mb-2', text.secondary)}>
                      {t('luggageCapacity')} *
                    </label>
                    <ThemedInput
                      type="number"
                      name="luggage_capacity"
                      value={newVehicle.luggage_capacity}
                      onChange={handleInputChange}
                      placeholder={t('luggageCapacity, 0 for unlimited')}
                      required
                      min="0"
                    />
                  </div>
                </div>

                {/* GPS Device Assignment */}
                <div>
                  <label className={cn('block text-sm font-medium mb-2', text.secondary)}>
                    {t('gpsDevice')}
                  </label>
                  <select
                    name="gps_device_id"
                    value={newVehicle.gps_device_id}
                    onChange={handleInputChange}
                    className={cn(
                      'w-full px-3 py-2 border rounded-md',
                      components.input.base,
                      components.input.focus
                    )}
                    disabled={devicesLoading}
                  >
                    <option value="">{t('noDevice')}</option>
                    <option value="none">{t('noDevice')}</option>
                    {availableDevices.map((device) => {
                      const isCurrentlyAssigned = editingVehicleId && device.vehicle_id === editingVehicleId;
                      const label = isCurrentlyAssigned
                        ? `${device.device_name} (${t('currentlyAssigned')})`
                        : device.device_name;

                      return (
                        <option key={device.id} value={device.id}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                  <p className={cn('text-xs mt-1', text.muted)}>
                    {t('gpsDeviceHelperText') || 'Assign a GPS device for real-time tracking'}
                  </p>
                  {devicesLoading && (
                    <p className={cn('text-xs mt-1', text.muted)}>
                      {t('loading')}...
                    </p>
                  )}
                </div>

                {accessibleServiceEnabled && (
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      name="accessible"
                      checked={Boolean(newVehicle.accessible)}
                      onChange={handleInputChange}
                      className="mt-1"
                    />
                    <div>
                      <div className={cn('text-sm font-medium', text.primary)}>
                        {t('accessibleVehicle')}
                      </div>
                      <div className={cn('text-xs mt-1', text.muted)}>
                        {t('accessibleVehicleHelp')}
                      </div>
                    </div>
                  </label>
                )}

                {accessibleServiceEnabled && newVehicle.accessible && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={cn('block text-sm font-medium mb-2', text.secondary)}>
                        {t('accessibleCapacity')}
                      </label>
                      <ThemedInput
                        type="number"
                        name="accessible_capacity"
                        value={newVehicle.accessible_capacity}
                        onChange={handleInputChange}
                        placeholder={t('accessibleCapacity')}
                        min="0"
                      />
                      <p className={cn('text-xs mt-1', text.muted)}>
                        {t('accessibleCapacityHelp')}
                      </p>
                    </div>
                    <div>
                      <label className={cn('block text-sm font-medium mb-2', text.secondary)}>
                        {t('accessibleSeatUsage')}
                      </label>
                      <select
                        name="accessible_seat_usage"
                        value={newVehicle.accessible_seat_usage}
                        onChange={handleInputChange}
                        className={cn(
                          'w-full px-3 py-2 border rounded-md',
                          components.input.base,
                          components.input.focus
                        )}
                      >
                        <option value="reserved_only">{t('accessibleSeatUsageReservedOnly')}</option>
                        <option value="shared_with_standard">{t('accessibleSeatUsageSharedWithStandard')}</option>
                      </select>
                      <p className={cn('text-xs mt-1', text.muted)}>
                        {t('accessibleSeatUsageHelp')}
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <label className={cn('block text-sm font-medium mb-2', text.secondary)}>
                    <ImageIcon className="inline w-4 h-4 mr-1" />
                    {t('vehicleImage')}
                  </label>
                  <div className="space-y-2">
                    
                    <div className="relative">
                      {/* Custom File Input */}
                      <input
                        type="file"
                        id="vehicle-picture"
                        name="picture"
                        onChange={handleFileChange}
                        accept="image/*"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <div className={cn(
                        'flex items-center justify-between px-3 py-2 border rounded-md cursor-pointer',
                        components.input.base,
                        components.input.focus
                      )}>
                        <span className={cn('text-sm', vehiclePicture ? text.primary : text.muted)}>
                          {vehiclePicture 
                            ? vehiclePicture.name 
                            : editingVehicleId && newVehicle.picture_url 
                              ? newVehicle.picture_url.split('/').pop()
                              : t('noFileChosen')
                          }
                        </span>
                        <span className={cn(
                          'px-3 py-1 text-sm rounded border ml-2',
                          components.button.secondary
                        )}>
                          {t('chooseFile')}
                        </span>
                      </div>
                      {vehiclePicture && (
                        <div className={cn('text-sm mt-1', text.muted)}>
                          {t('newImageSelected')}: {vehiclePicture.name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <ThemedButton
                    type="submit"
                    variant="primary"
                    icon={Save}
                  >
                    {editingVehicleId ? t('update') : t('addVehicle')}
                  </ThemedButton>
                  <ThemedButton
                    type="button"
                    onClick={handleCancel}
                    variant="secondary"
                    icon={BookX}
                  >
                    {t('Cancel')}
                  </ThemedButton>

                </div>
              </form>
            </ThemedCard.Body>
          </ThemedCard>
        )}
      </div>

      {/* Scrollable Vehicle List */}
      <div className={cn('flex-grow overflow-y-auto p-6', background.secondary)}>
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className={cn('text-lg', text.muted)}>{t('loading')}...</div>
          </div>
        ) : filteredVehicles.length === 0 ? (
          <div className="text-center py-12">
            <div className={cn('text-gray-400 text-lg mb-2', text.muted)}>
              {searchTerm ? t('noVehiclesFound') : t('noVehiclesYet')}
            </div>
            {!searchTerm && (
              <ThemedButton
                onClick={handleAddVehicleClick}
                variant="primary"
                icon={Plus}
                className="mt-4"
              >
                {t('addFirstVehicle')}
              </ThemedButton>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVehicles.map((vehicle) => {
              const isSelected = selectedVehicleId === vehicle.id;
              
              return (
                <ThemedCard
                  key={vehicle.id}
                  hoverable
                  selected={isSelected}
                  onClick={() => handleVehicleSelect(vehicle.id)}
                  className="cursor-pointer transition-all duration-200"
                >
                  {/* Vehicle Image */}
                  {vehicle.picture_url ? (
                    <div className="w-full h-48 mb-4 overflow-hidden rounded-lg bg-gray-50 dark:bg-gray-800 flex items-center justify-center">
                      <AppImage
                        src={vehicle.picture_url}
                        alt={vehicle.vehicle_number}
                        crossOrigin="anonymous"
                        fallbackSrc={null}
                        className="max-w-full max-h-full object-contain hover:scale-105 transition-transform duration-200"
                      />
                    </div>
                  ) : (
                    <div className={cn('w-full h-48 mb-4 flex items-center justify-center rounded-lg border-2 border-dashed', border.default)}>
                      <ImageIcon className={cn('w-12 h-12', text.muted)} />
                    </div>
                  )}
                  
                  {/* Vehicle Details */}
                  <div className="space-y-2">
                    <h3 className={cn('font-semibold text-lg', text.primary)}>
                      {vehicle.vehicle_number}
                    </h3>
                    <div className="flex justify-between items-center">
                      <span className={cn('text-sm', text.secondary)}>
                        {t('capacity')}: <span className={cn('font-medium', text.primary)}>{vehicle.capacity}</span>
                      </span>
                      <span className={cn('text-sm', text.secondary)}>
                        {t('luggageCapacity')}: <span className={cn('font-medium', text.primary)}>{vehicle.luggage_capacity}</span>
                      </span>
                    </div>
                    {accessibleServiceEnabled && vehicle.is_accessible && (
                      <div className={cn('text-sm', text.secondary)}>
                        {t('accessibleCapacity')}: <span className={cn('font-medium', text.primary)}>{vehicle.accessible_capacity || 0}</span>
                      </div>
                    )}
                    {accessibleServiceEnabled && vehicle.is_accessible && (
                      <div className={cn('text-sm', text.secondary)}>
                        {t('accessibleSeatUsage')}: <span className={cn('font-medium', text.primary)}>
                          {vehicle.accessible_seat_usage === 'shared_with_standard'
                            ? t('accessibleSeatUsageSharedWithStandard')
                            : t('accessibleSeatUsageReservedOnly')}
                        </span>
                      </div>
                    )}

                    {/* GPS Device Status */}
                    {(() => {
                      const assignedDevice = allDevices.find(d => d.vehicle_id === vehicle.id);
                      if (assignedDevice) {
                        const health = gpsDeviceService.getDeviceHealth(assignedDevice);
                        const healthIcon = gpsDeviceService.getHealthIcon(health);
                        return (
                          <div className={cn('text-sm mt-2 pt-2 border-t', border.default)}>
                            <span className={cn(text.secondary)}>
                              {t('gpsDevice')}:
                            </span>
                            <div className="flex items-center mt-1">
                              <span className="mr-1">{healthIcon}</span>
                              <span className={cn('font-medium', text.primary)}>
                                {assignedDevice.device_name}
                              </span>
                            </div>
                          </div>
                        );
                      } else {
                        return (
                          <div className={cn('text-sm mt-2 pt-2 border-t', border.default)}>
                            <span className={cn(text.muted)}>
                              ⚠️ {t('noDeviceAssigned')}
                            </span>
                          </div>
                        );
                      }
                    })()}
                  </div>

                  {/* Auto-assignment mode badge + accessible badge */}
                  <div className={cn('mt-2 pt-2 border-t flex items-center gap-2 flex-wrap', border.default)}>
                    <span className={cn(
                      'px-2 py-0.5 rounded-full text-xs font-medium',
                      vehicle.auto_assignment_mode === 'manual_only'
                        ? 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300'
                        : vehicle.auto_assignment_mode === 'request_only'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                          : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                    )}>
                      {vehicle.auto_assignment_mode === 'manual_only'
                        ? t('vehicleAssignment.manualOnly')
                        : vehicle.auto_assignment_mode === 'request_only'
                          ? t('vehicleAssignment.requestOnly')
                          : t('vehicleAssignment.eligible')}
                    </span>
                    {accessibleServiceEnabled && vehicle.is_accessible && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                        {t('accessible')}
                      </span>
                    )}
                  </div>

                  {/* Action Buttons */}
                  {isSelected && (
                    <>
                    <div className="flex justify-end space-x-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <ThemedButton
                        size="sm"
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(vehicle);
                        }}
                        icon={Edit}
                      >
                        {t('edit')}
                      </ThemedButton>
                      <ThemedButton
                        size="sm"
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveTab('policy');
                        }}
                      >
                        {t('vehicleAssignment.policyTab')}
                      </ThemedButton>
                      <ThemedButton
                        size="sm"
                        variant="danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(vehicle);
                        }}
                        icon={Delete}
                      >
                        {t('delete')}
                      </ThemedButton>
                    </div>
                    <OperationalUnavailabilityPanel
                      entityId={currentEntity?.entityId}
                      targetType="vehicle"
                      targetId={vehicle.id}
                      compact
                    />
                    </>
                  )}
                </ThemedCard>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {/* NOTE: Modal is outside the tab fragment so it renders regardless of active tab */}
      </> /* end vehicles tab fragment */}
      <ConfirmDialogShell
        isOpen={showDeleteConfirmation}
        onClose={() => !isDeleting && setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteConfirm}
        title={t('confirmDelete')}
        confirmLabel={t('Confirm')}
        confirmVariant="danger"
        confirmIcon={Delete}
        isLoading={isDeleting}
        isDisabled={isDeleting}
        showIcon={true}
        iconVariant="danger"
        closeOnBackdropClick={!isDeleting}
      >
        <p className={cn('text-sm', text.secondary)}>
          {t('areYouSure')}
        </p>
      </ConfirmDialogShell>
    </div>
  );
};

export default VehicleList;
